import {create} from 'zustand'
import {ChatMessage, InteractionState, Suggestion} from '@features/ai-chatbot/types.ts'

type ChatState = {
    // UI state
    isOpen: boolean

    // Messages
    messages: ChatMessage[]

    // Streaming state
    isStreaming: boolean
    currentStreamContent: string

    // Interaction state
    interactionState: InteractionState
    suggestions: Suggestion[]

    // Actions
    setIsOpen: (isOpen: boolean) => void
    toggleOpen: () => void
    addMessage: (message: ChatMessage) => void
    setStreaming: (isStreaming: boolean) => void
    setCurrentStreamContent: (content: string) => void
    appendStreamContent: (delta: string) => void
    setInteractionState: (state: InteractionState) => void
    setSuggestions: (suggestions: Suggestion[]) => void
    finalizeAssistantMessage: () => void
    setLastMessageAsError: () => void
    reset: () => void
}

const initialState = {
    isOpen: false,
    messages: [] as ChatMessage[],
    isStreaming: false,
    currentStreamContent: '',
    interactionState: {type: 'idle'} as InteractionState,
    suggestions: [] as Suggestion[]
}

export const useChatStore = create<ChatState>((set, get) => ({
    ...initialState,

    setIsOpen: isOpen => set({isOpen}),

    toggleOpen: () => set(state => ({isOpen: !state.isOpen})),

    addMessage: message => set(state => ({messages: [...state.messages, message]})),

    setStreaming: isStreaming => set({isStreaming}),

    setCurrentStreamContent: content => set({currentStreamContent: content}),

    appendStreamContent: delta => set(state => ({currentStreamContent: state.currentStreamContent + delta})),

    setInteractionState: interactionState => set({interactionState}),

    setSuggestions: suggestions => set({suggestions}),

    finalizeAssistantMessage: () => {
        const {currentStreamContent, messages} = get()
        if (currentStreamContent) {
            const lastIdx = messages.length - 1
            if (lastIdx >= 0 && messages[lastIdx].role === 'assistant') {
                set(state => {
                    const updatedMessages = [...state.messages]
                    updatedMessages[lastIdx] = {
                        ...updatedMessages[lastIdx],
                        content: currentStreamContent
                    }
                    return {
                        messages: updatedMessages,
                        currentStreamContent: '',
                        isStreaming: false
                    }
                })
            }
        }
    },

    setLastMessageAsError: () => {
        const {messages} = get()
        const lastIdx = messages.length - 1
        if (lastIdx >= 0 && messages[lastIdx].role === 'assistant') {
            set(state => {
                const updatedMessages = [...state.messages]
                updatedMessages[lastIdx] = {
                    ...updatedMessages[lastIdx],
                    isError: true
                }
                return {
                    messages: updatedMessages,
                    currentStreamContent: '',
                    isStreaming: false
                }
            })
        }
    },

    reset: () => set(state => ({...initialState, isOpen: state.isOpen}))
}))
